import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, Icon } from '@mui/material'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import DataChip from '../components/DataChip'
import DataTable from '../components/DataTable'
import CloseWarning from './CloseWarning'
import { useMutation, useQueryClient } from 'react-query'

// API
import { postInviteStaffBulk } from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxButton from 'components/custom/AdxButton'

const MultipleStaffTable = (props) => {
  const queryClient = useQueryClient()

  const navigate = useNavigate()
  const [pageState, setPageState] = useState('IMPORT')

  const staffData = props?.state?.staffData
  const validateData = staffData.staff_count - staffData.errors_count

  const handleCloseModal = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }
    navigate(-1)
  }

  const mutation = useMutation(postInviteStaffBulk, {
    onSuccess: (response) => {
      queryClient.invalidateQueries('staffListData')
      setPageState('CONFIRMATION')
    },
    onError: (error) => {
      if (error.status === 422) {
        console.log(error)
      } else {
        console.log(error)
      }
    },
  })

  const handleConfirmInvite = () => {
    let filteredValue = staffData.staff.filter((filterItem) => filterItem.errors.length === 0)
    let values = filteredValue.map((normalItem, index) => {
      return {
        name: normalItem.name,
        email: normalItem.email,
        designation: normalItem.designation,
        department: normalItem.department,
      }
    })
    let newValues = {
      new_staff: values,
    }
    // console.log(newValues)
    mutation.mutate(newValues)
  }

  const handleInviteStaff = () => {
    setPageState('SUCCESSFUL_REQUEST')
  }

  const CustomHeader = () => {
    return (
      <>
        <Box
          sx={{
            py: 1,
            px: 4,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Icon sx={{ color: 'alternate.main', mr: 1, fontSize: 24 }}>group_add_alt_1</Icon>
            <Typography variant="h4">Invite multiple staffs</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '10%' }}></Box>
          <CloseWarning />
        </Box>
      </>
    )
  }

  if (pageState === 'CONFIRMATION') {
    return (
      <AdxInfoModal
        open={true}
        muiIcon="group_add"
        iconColor="alternate.main"
        title={`${staffData && validateData} ${
          staffData && validateData > 1 ? 'Staffs' : 'Staff'
        } Invited`}
        subtitle={`Your staff has been invited through the email registered. You can check with them to verify the registration.`}
        modalWidth="500px"
        footerButtons
        firstBtnLabel={'Close'}
        firstBtnHandle={() => navigate('/app/management-centre/staff-management/employee-file')}
      ></AdxInfoModal>
    )
  }

  if (pageState === 'SUCCESSFUL_REQUEST') {
    return (
      <AdxInfoModal
        open={true}
        muiIcon="error"
        iconColor="warning.main"
        title={`Invite ${staffData && validateData} ${
          staffData && validateData > 1 ? 'staffs' : 'staff'
        }?`}
        subtitle={`You’re going to send an invitation to ${staffData && validateData} ${
          staffData && validateData > 1 ? 'staffs' : 'staff'
        }. If you proceed, these staff will receive an invitation to join your organization by email.`}
        modalWidth="500px"
        footerButtons
        firstBtnLabel={'Send invitation'}
        firstBtnHandle={handleConfirmInvite}
        secondBtnLabel={'Cancel'}
        secondBtnHandle={() => navigate(-1)}
      ></AdxInfoModal>
    )
  }

  const handleReuploadCSV = () => {
    navigate(-1)
  }

  return (
    <AdxFormModal
      open={true}
      onClose={handleCloseModal}
      handleClose={handleCloseModal}
      disableEscapeKeyDown
      disableAutoFocus
      title={'Invite multiple staffs'}
      sxModal={{
        minWidth: { xs: 'none', sm: 'calc(100vw - 30px)', lg: '1000px' },
        width: { xs: 'calc(100vw - 30px)', lg: 'calc(100vw - 1500px)' },
      }}
      customHeader={<CustomHeader />}
    >
      <>
        {pageState === 'IMPORT' && (
          <Box
            sx={{
              width: '100%',
              minHeight: '300px',

              display: 'flex',
              flexDirection: 'column',
              rowGap: 2,
            }}
          >
            <Box
              sx={{
                width: 1,

                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="body2" color="dark-gray-text.main">
                  Step 3
                </Typography>

                <Typography mr={1} variant="body2" color="light-gray-text.main">
                  Check the details uploaded. Then, send invites to the following staff.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'flex-start',
                  rowGap: 0.5,
                }}
              >
                <DataChip
                  sx={{ mr: 2 }}
                  color="info.main"
                  content={`${staffData && staffData.staff_count} data found`}
                />
                <DataChip
                  color="error.main"
                  content={`${staffData && staffData.errors_count} ${
                    staffData && staffData.errors_count > 1 ? 'errors' : 'error'
                  } found`}
                />
              </Box>
            </Box>
            <Box>
              <Box
                sx={{
                  width: '100%',
                  height: '45px',
                  backgroundColor: 'gray-background.main',
                  display: 'flex',
                  py: 1,
                  px: 3,
                  alignItems: 'center',
                  overflow: 'auto',
                }}
              >
                <Box sx={{ minWidth: '150px' }} width={1 / 4}>
                  <Typography variant="body1" color="dark-gray-text.main">
                    Staff Name
                  </Typography>
                </Box>
                <Box sx={{ minWidth: '150px' }} width={1 / 4}>
                  <Typography variant="body1" color="dark-gray-text.main">
                    Email
                  </Typography>
                </Box>
                <Box sx={{ minWidth: '150px' }} width={1 / 4}>
                  <Typography align="center" variant="body1" color="dark-gray-text.main">
                    Job Designation
                  </Typography>
                </Box>
                <Box sx={{ minWidth: '150px' }} width={1 / 4}>
                  <Typography align="center" variant="body1" color="dark-gray-text.main">
                    Department
                  </Typography>
                </Box>
              </Box>
              <Box>
                <DataTable staffData={staffData} />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                flexDirection: 'row',
                alignItems: 'center',

                width: '100%',
                height: '50px',
                columnGap: 1,
              }}
            >
              <AdxButton
                variant={'outlined'}
                onClick={handleReuploadCSV}
                sx={{ width: { xs: 1 / 2, sm: 1 / 4 } }}
              >
                Re-upload CSV
              </AdxButton>
              <AdxButton
                variant={'contained'}
                onClick={handleInviteStaff}
                sx={{ width: { xs: 1 / 2, sm: 2 / 5 } }}
                disabled={staffData && validateData === 0 ? true : false}
              >
                Invite Staff ({staffData && validateData})
              </AdxButton>
            </Box>
          </Box>
        )}
      </>
    </AdxFormModal>
  )
}

export default MultipleStaffTable
